import React from 'react';
import { Link } from 'react-router-dom';
import { useHistory, useLocation, useParams } from 'react-router';

import getSettingItems from '@/structure/settings';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarMenuSub,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';

import { NavUser } from '@/components/ui/nav-user';

import { excludedFromPropertyRelatedPaths } from '@/utils/specificPaths';
import {
  privateNavbarMenu,
  bmPlusNavbarMenu,
  servicesNavbarMenu,
} from '@/structure/menu';

import useDashboardLinkPrefix from '@/hooks/useDashboardLinkPrefix';
import { getPropertyAddress } from '@/utils/properties';
import BoligmappaHeaderLogo from '@/assets/images/BM-header-logo.svg';
import Home from '@/assets/icons/Home';
import HomeBoldIcon from '@/assets/icons/HomeBoldIcon';
import { getDashboardLinkPrefix } from '@/utils/routes';
import { MenuIcon } from 'lucide-react';
import ProfileIcon from '@/assets/icons/ProfileIcon';

export function AppSidebar({
  ownedProperties,
  sharedProperties,
  activeProperty,
  activeUser,
  documentsToSigned,
}) {
  const history = useHistory();
  const { pathname } = useLocation();
  const dashboardLinkPrefix = useDashboardLinkPrefix();
  const { boligmappaNumber } = useParams('boligmappaNumber');
  const propertyMenuItems = privateNavbarMenu
    .filter((menuItem) => !menuItem.mobileOnly)
    .filter(
      (menuItem) =>
        !menuItem.documentsToSignedOnly ||
        documentsToSigned?.some(
          (el) => activeProperty?.boligmappaNumber === el.boligmappaNumber
        )
    );

  const profileMenuSections = getSettingItems({
    dashboardLinkPrefix,
    propertyDashboardLinkPrefix: getDashboardLinkPrefix(
      activeProperty?.boligmappaNumber
    ),
  })
    .filter(({ mobileOnly }) => !mobileOnly)
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (el) => !el.propertyRequired || activeProperty?.owned
      ),
    }));

  const onPropertyChange = (propertyItem) => {
    history.push(`/dashboard/${propertyItem.boligmappaNumber}/home`);
  };

  const setHomeIconLink = () => {
    if (!!ownedProperties?.length || !!sharedProperties?.length) {
      return `/dashboard/overview`;
    }
    return '/personal-area/dashboard/boligmappa-plus';
  };

  return (
    <Sidebar className="font-circular z-[999]">
      <Link to={setHomeIconLink()} className="flex content-start">
        <img
          className="max-h-[18px] m-4 mb-[5px] select-none [user-drag:none] [user-select:none] [touch-callout:none]"
          src={BoligmappaHeaderLogo}
          alt="Logo"
        />
      </Link>
      <SidebarContent>
        {!!ownedProperties?.length && (
          <SidebarGroup>
            <SidebarMenuButton
              className="data-[active=true]:text-primary"
              isActive={pathname.includes('dashboard/overview')}
              asChild
            >
              <Link
                className="flex justify-between data-[active=true]:shadow data-[active=true]:rounded-lg"
                to="/dashboard/overview"
              >
                <div className="flex items-center gap-2">
                  <MenuIcon className="text-sidebarMenu" width={18} />
                  Hjem
                </div>
              </Link>
            </SidebarMenuButton>
            <SidebarGroupLabel>Dine boliger</SidebarGroupLabel>
            {ownedProperties?.map((propertyItem, index) => (
              <Collapsible
                key={propertyItem.id}
                open={
                  !excludedFromPropertyRelatedPaths.some((urlString) =>
                    pathname.includes(urlString)
                  ) && propertyItem.boligmappaNumber === boligmappaNumber
                }
                className="group/collapsible"
              >
                <CollapsibleTrigger
                  asChild
                  onClick={() => onPropertyChange(propertyItem)}
                >
                  <SidebarMenuButton>
                    <SidebarGroupLabel className="justify-between w-full p-0">
                      <div className="flex gap-2 text-sidebarMenu">
                        {index === 0 ? <HomeBoldIcon /> : <Home />}
                        {getPropertyAddress(propertyItem)}
                      </div>
                      {documentsToSigned &&
                        documentsToSigned.some(
                          (doc) =>
                            doc.boligmappaNumber ===
                            propertyItem.boligmappaNumber
                        ) && (
                          <span className="bg-primary rounded-full h-[7px] w-[7px]" />
                        )}
                    </SidebarGroupLabel>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenuSub className="mr-0 pr-0">
                      <SidebarMenu>
                        {propertyMenuItems.map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton
                              className="data-[active=true]:text-primary"
                              asChild
                              isActive={
                                !pathname.includes('personal-area') &&
                                pathname ===
                                  `${dashboardLinkPrefix}/${item.link}`
                              }
                            >
                              <Link
                                className="flex justify-between data-[active=true]:shadow data-[active=true]:rounded-lg"
                                to={`${getDashboardLinkPrefix(
                                  activeProperty?.boligmappaNumber ||
                                    ownedProperties[0].boligmappaNumber
                                )}/${item.link}`}
                              >
                                <div className="flex items-center gap-2">
                                  {!pathname.includes('personal-area') &&
                                  pathname ===
                                    `${dashboardLinkPrefix}/${item.link}` ? (
                                    <item.activeIcon />
                                  ) : (
                                    <item.icon />
                                  )}
                                  <span>{item.title}</span>
                                </div>
                                {item.documentsToSignedOnly && (
                                  <span className="text-white w-5 h-5 text-center bg-primary rounded-full">
                                    {
                                      documentsToSigned.filter(
                                        (doc) =>
                                          doc.boligmappaNumber ===
                                          propertyItem.boligmappaNumber
                                      ).length
                                    }
                                  </span>
                                )}
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarMenuSub>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            ))}
            <SidebarMenuButton
              className="data-[active=true]:text-primary"
              isActive={pathname.includes(
                'personal-area/dashboard/more/account'
              )}
              asChild
            >
              <Link
                className="flex justify-between data-[active=true]:shadow data-[active=true]:rounded-lg"
                to="/personal-area/dashboard/more/account"
              >
                <div className="flex items-center gap-2 text-[12px] font-medium">
                  <ProfileIcon className="text-sidebarMenu" />
                  Din konto
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarGroup>
        )}

        {!!sharedProperties?.length && (
          <SidebarGroup>
            <SidebarGroupLabel>Delt med deg</SidebarGroupLabel>
            {sharedProperties.map((sharedPropertyItem) => (
              <Collapsible
                key={sharedPropertyItem.id}
                open={
                  !excludedFromPropertyRelatedPaths.some((urlString) =>
                    pathname.includes(urlString)
                  ) && sharedPropertyItem.boligmappaNumber === boligmappaNumber
                }
                className="group/collapsible"
              >
                <CollapsibleTrigger
                  asChild
                  onClick={() => onPropertyChange(sharedPropertyItem)}
                >
                  <SidebarMenuButton>
                    <SidebarGroupLabel className="p-0 gap-2 text-sidebarMenu">
                      <Home />
                      {getPropertyAddress(sharedPropertyItem)}
                    </SidebarGroupLabel>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenuSub>
                      <SidebarMenu>
                        {propertyMenuItems.map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton
                              className="data-[active=true]:text-primary"
                              asChild
                              isActive={
                                !pathname.includes('personal-area') &&
                                pathname.includes(item.link)
                              }
                            >
                              <Link
                                className="data-[active=true]:shadow data-[active=true]:rounded-lg"
                                to={`${getDashboardLinkPrefix(
                                  activeProperty?.boligmappaNumber
                                )}/${item.link}`}
                              >
                                {!pathname.includes('personal-area') &&
                                pathname.includes(item.link) ? (
                                  <item.activeIcon />
                                ) : (
                                  <item.icon />
                                )}
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarMenuSub>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </SidebarGroup>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>Boligmappa+</SidebarGroupLabel>
          <SidebarMenu>
            {bmPlusNavbarMenu.map(
              (item) =>
                (item.propertyRequired &&
                  !activeProperty.id &&
                  !ownedProperties?.length) || (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname.includes(item.link)}
                    >
                      <Link
                        to={
                          !item.propertyRequired
                            ? item.link
                            : `${getDashboardLinkPrefix(
                                activeProperty?.boligmappaNumber ||
                                  ownedProperties[0]?.boligmappaNumber
                              )}/${item.link}`
                        }
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
            )}
          </SidebarMenu>
        </SidebarGroup>
        {activeProperty?.boligmappaNumber && (
          <SidebarGroup>
            <SidebarGroupLabel>Tjenester</SidebarGroupLabel>
            <SidebarMenu>
              {servicesNavbarMenu.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname.includes(item.link)}
                  >
                    {item.isExternalLink ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </a>
                    ) : (
                      <Link
                        to={`${getDashboardLinkPrefix(
                          activeProperty?.boligmappaNumber
                        )}/${item.link}`}
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          user={{
            name: activeUser?.fullName,
            email: activeUser?.email,
            avatarNameLetters:
              activeUser?.firstName !== undefined &&
              activeUser?.firstName[0] + activeUser?.lastName[0],
          }}
          profileMenuSections={profileMenuSections}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
